// The meeting's in-place update of an issue it filed ("Ticket aktualisieren",
// a late answer's re-draft; owner 2026-10-30: an update never drops a
// section). The body AS IT STANDS is the base on every body; the fresh render
// (the newer evaluation's draft, rendered as the filing renders it) only adds
// to it.
//
// UNITS. A `## ` section. In the three sections the draft owns (`## What
// you need`, `## Business outcome`, `## Acceptance criteria`): each
// paragraph, and each `- ` row of a list (a list may open with a lead line,
// `EPIC_LIST_LEADS`' "Teile:"). In `## From meeting`: each row of "Im Meeting
// genannt", "Zitate" and "Nicht im Umfang" (`meetingBlockRowsOf`).
//
// COVERED. Unit u is covered by unit v when every content stem of u is v's
// (`contentStemsOf`) and every ANCHOR of u is v's: an acronym ("KPI"), a
// number with a separator or of two digits ("4.2", "2027"), an issue number
// ("#42"), a quoted span of 3 to 60 characters. Both are read without a list
// marker and without a lead label ("Idee:", `stripLeadLabel`). A unit with
// no stem is covered by the same words. A quote is covered by a quote holding
// its words (`containsQuote`).
//
// RULES (`mergeMeetingUpdate`):
// 1. Every section the draft does not own, the preamble and the backlink are
//    carried verbatim (`renderIssueBody`'s carry rule).
// 2. An owned section starts from the CURRENT section. Each fresh unit no
//    current unit covers is appended: a paragraph after the current
//    paragraphs, a row at the end of the list with the same lead, a new list
//    whole at the end. On Loom's own bytes (`loomOwned`: the stored hash
//    matches) a fresh unit replaces a current one it covers. A fresh section
//    the body lacks is added; a current section fresh lacks is kept.
// 3. The block starts from the CURRENT block (none when a human removed it):
//    its managed lines as `parts` say (`patchMeetingBlock`), then the fresh
//    rows it lacks (`unionMeetingBlockContent`).
// 4. The DoR marker stays last.
// 5. RETRACT, on Loom's own bytes only, never a whole section: the lead label
//    of agreed work (`retract.ideaLead`, stripped in place), a unit that IS a
//    value the judge rejected as another subject's, contradicting or out of
//    scope (`retract.rejected`), and a unit that IS a row cut as another
//    meeting issue's that that issue already says (`retract.belongsTo`). IS:
//    the same anchors and nearly the same stems (`sameSaid`), never a short
//    row whose words a longer value holds. A unit the fresh render still says
//    stays. A paragraph written as a list's lead outlives its retracted rows.
//    On an edited body each is kept and `flagged`.
// 6. RESTORE: on Loom's own bytes, each unit of `restore` (the filing
//    revision's render) that the result does not cover comes back.
// 7. POST-CHECK: every current section, and every current unit's stems and
//    anchors (a list's sentence lead is a unit here), are in the result,
//    except what was retracted; whatever is
//    missing is `dropped`, and the caller writes nothing.
// 8. Idempotent: merging the fresh render again changes nothing, and a body
//    merged with itself is itself.
//
// Ships to the browser like the rest of this module: no `node:` API, no regex
// lookbehind.

import {
  meetingBlockRowsOf,
  patchMeetingBlock,
  unionMeetingBlockContent,
  withoutMeetingBlockRows,
  type MeetingBlockParts,
  type MeetingBlockRow,
  type MeetingBlockRowPart,
} from "./meeting-block.js";
import { containsQuote, meetingQuoteText, type MeetingTextLang } from "./neutralise.js";
import { parseIssueBody, sectionOf, type ParsedIssueBody } from "./parse.js";
import { renderIssueBody, type OwnedSections } from "./render.js";
import { contentStemsOf } from "./stems.js";
import { EPIC_LIST_LEADS, LIST_ROW_RE, stripLeadLabel, stripListMarker, type KnownKey } from "./tokens.js";

/** The sections a meeting draft writes: the ones an update merges row by row. */
export const MERGED_SECTIONS: readonly KnownKey[] = ["what_you_need", "business_outcome", "acceptance_criteria"];

/** Why a unit left the body (or, on an edited body, would have). */
export type MergeRetractReason =
  | "idea_lead"
  | "judge_one_subject"
  | "judge_no_contradiction"
  | "judge_out_of_scope"
  | "belongs_to";

/** The judge's rules a retract follows (`meeting_judge`'s `why`). */
export type MergeRejectedWhy = "one_subject" | "no_contradiction" | "out_of_scope";

/** What an update may take out of Loom's own bytes (rule 5). */
export interface MergeRetract {
  /** The row's reason is not `idea`: a value's "Idee:" label goes. */
  readonly ideaLead?: boolean;
  /** Values the judge rejected for these rules. */
  readonly rejected?: readonly { readonly text: string; readonly why: MergeRejectedWhy }[];
  /**
   * Rows cut from this issue as another meeting issue's (`scopedOut`), each
   * with what that issue says now (its body, or its successor's fresh
   * render): a unit goes only when it is one of the rows AND `says` covers it.
   */
  readonly belongsTo?: readonly { readonly iid: number; readonly rows: readonly string[]; readonly says: string }[];
}

export interface MergeMeetingUpdateOptions {
  /** The body is still the one Loom last wrote (its stored hash). */
  readonly loomOwned: boolean;
  /** The language of a section the body does not have yet. */
  readonly lang: MeetingTextLang;
  /** Loom's managed lines of the block (`desiredBlockPartsOf`); a part left out keeps its bytes. */
  readonly parts: MeetingBlockParts;
  readonly retract?: MergeRetract;
  /** The filing revision's render: what an earlier update dropped comes back from it. */
  readonly restore?: string | null;
}

/** One unit, named by its section (or block part) heading. */
export interface MergeUnit {
  readonly section: string;
  readonly text: string;
}

export interface MergeRetracted extends MergeUnit {
  readonly reason: MergeRetractReason;
  /** `belongs_to`: the issue it belongs to. */
  readonly iid?: number;
}

export interface MeetingUpdateMerge {
  readonly body: string;
  /** Current units the fresh render does not say, kept. */
  readonly kept: readonly MergeUnit[];
  /** Fresh units added (or, on Loom's bytes, put in place of one they cover). */
  readonly added: readonly MergeUnit[];
  readonly retracted: readonly MergeRetracted[];
  readonly restored: readonly MergeUnit[];
  /** On an edited body: what a retract would have taken out, kept. */
  readonly flagged: readonly MergeRetracted[];
  /** The post-check: current units the result lost. Non-empty: write nothing (`would_drop`). */
  readonly dropped: readonly MergeUnit[];
}

// ─── Coverage ─────────────────────────────────────────────────────────────

/** What a unit says, as coverage reads it. */
interface Said {
  readonly stems: ReadonlySet<string>;
  readonly anchors: ReadonlySet<string>;
  /** Its words, space-joined and space-framed, for a unit with no stem. */
  readonly words: string;
}

const WORD_SPLIT = /[^\p{L}\p{N}]+/u;
const ANCHOR_NUMBER = /\d+(?:[.,:/-]\d+)+|\d{2,}/gu;
const ANCHOR_ISSUE = /#\d+/gu;
const ANCHOR_QUOTED = /«([^»\n]{3,60})»|„([^“”\n]{3,60})[“”]|“([^”\n]{3,60})”|"([^"\n]{3,60})"/gu;
const UPPER = /\p{Lu}/gu;

/** The anchors of a text: acronyms, numbers, issue numbers, quoted spans. */
export const anchorsOf = (text: string): Set<string> => {
  const out = new Set<string>();
  for (const word of text.split(WORD_SPLIT)) {
    if ((word.match(UPPER) ?? []).length >= 2) out.add(`a:${word}`);
  }
  for (const m of text.matchAll(ANCHOR_NUMBER)) out.add(`n:${m[0]}`);
  for (const m of text.matchAll(ANCHOR_ISSUE)) out.add(`i:${m[0]}`);
  for (const m of text.matchAll(ANCHOR_QUOTED)) {
    const inner = (m[1] ?? m[2] ?? m[3] ?? m[4] ?? "").trim().toLowerCase();
    if (inner !== "") out.add(`q:${inner}`);
  }
  return out;
};

/** A unit's text without a list marker and without a lead label. */
const plainOf = (text: string): string => stripLeadLabel(stripListMarker(text)).text;

const saidOf = (text: string): Said => {
  const plain = plainOf(text);
  const words = plain.toLowerCase().split(WORD_SPLIT).filter((w) => w !== "");
  return { stems: contentStemsOf(plain), anchors: anchorsOf(plain), words: ` ${words.join(" ")} ` };
};

/** Does `v` say everything `u` says (see the header)? */
const covers = (v: Said, u: Said): boolean => {
  for (const a of u.anchors) if (!v.anchors.has(a)) return false;
  if (u.stems.size === 0) return u.words.trim() === "" || v.words.includes(u.words);
  for (const s of u.stems) if (!v.stems.has(s)) return false;
  return true;
};

/**
 * Do `a` and `b` say the same thing: the same anchors and nearly the same
 * stems (Jaccard ≥ `NEAR_SAME`)? A retract takes out a unit only when it IS
 * the rejected or cut value, never because a longer value holds its words: a
 * catch-all the judge rejected names the issue's own rows too.
 */
const NEAR_SAME = 0.8;
const sameSaid = (a: Said, b: Said): boolean => {
  if (a.anchors.size !== b.anchors.size) return false;
  for (const x of a.anchors) if (!b.anchors.has(x)) return false;
  if (a.stems.size === 0 || b.stems.size === 0) return a.words === b.words;
  let shared = 0;
  for (const s of a.stems) if (b.stems.has(s)) shared += 1;
  return shared / (a.stems.size + b.stems.size - shared) >= NEAR_SAME;
};

/** Is `text` covered by one of `by`? */
export const unitCovered = (text: string, by: readonly string[]): boolean => {
  const u = saidOf(text);
  return by.some((v) => covers(saidOf(v), u));
};

/** The block's cover rule: a quote by a quote holding its words, any other row by stems and anchors. */
const blockCovered = (part: MeetingBlockRowPart, text: string, rows: readonly string[]): boolean =>
  part === "quotes" ? rows.some((r) => containsQuote(text, r)) : unitCovered(text, rows);

// ─── An owned section's units ─────────────────────────────────────────────

type Block =
  | { kind: "para"; text: string }
  | { kind: "list"; lead: string | null; rows: string[] };

const isRow = (line: string): boolean => LIST_ROW_RE.test(line);

/** A section's body as paragraphs and lists, split at blank lines. */
const blocksOf = (body: string | null): Block[] => {
  if (body === null) return [];
  const out: Block[] = [];
  let chunk: string[] = [];
  const flush = (): void => {
    if (chunk.length === 0) return;
    if (chunk.every(isRow)) out.push({ kind: "list", lead: null, rows: chunk });
    else if (chunk.length >= 2 && chunk.slice(1).every(isRow)) {
      out.push({ kind: "list", lead: chunk[0]!, rows: chunk.slice(1) });
    } else out.push({ kind: "para", text: chunk.join("\n") });
    chunk = [];
  };
  for (const line of body.split("\n")) {
    if (line.trim() === "") flush();
    else chunk.push(line);
  }
  flush();
  return out;
};

const textOfBlocks = (blocks: readonly Block[]): string =>
  blocks
    .map((b) => (b.kind === "para" ? b.text : [...(b.lead === null ? [] : [b.lead]), ...b.rows].join("\n")))
    .filter((t) => t.trim() !== "")
    .join("\n\n");

/** Every unit of a section: its paragraphs and its rows. */
const unitsOf = (blocks: readonly Block[]): string[] =>
  blocks.flatMap((b) => (b.kind === "para" ? [b.text] : b.rows));

const LEAD_LABELS_OF_LISTS: ReadonlySet<string> = new Set(
  Object.values(EPIC_LIST_LEADS).flatMap((l) => [l.details, l.parts].map((t) => t.trim().toLowerCase())),
);

/** A list's lead that is a sentence of its own (a paragraph written right above the rows), not a label like "Teile:". */
const isSentenceLead = (lead: string | null): lead is string =>
  lead !== null && lead.trim() !== "" && !LEAD_LABELS_OF_LISTS.has(lead.trim().toLowerCase());

/** The units the post-check holds the result to: `unitsOf` plus every sentence lead. */
const checkedUnitsOf = (blocks: readonly Block[]): string[] =>
  blocks.flatMap((b) => (b.kind === "para" ? [b.text] : [...(isSentenceLead(b.lead) ? [b.lead] : []), ...b.rows]));

const leadKey = (lead: string | null): string => (lead === null ? "" : lead.trim().toLowerCase());

/** The section's name as a heading line writes it (`## Was es braucht` → `Was es braucht`). */
const nameOf = (heading: string): string => heading.replace(/^##\s*/u, "").trim();

// ─── The merge ────────────────────────────────────────────────────────────

/** What one merge reports, filled as it goes. */
interface Report {
  readonly kept: MergeUnit[];
  readonly added: MergeUnit[];
  readonly retracted: MergeRetracted[];
  readonly restored: MergeUnit[];
  readonly flagged: MergeRetracted[];
}

/** Why a current unit would leave the body (rule 5), or null. Never one the fresh render says. */
const retractOf = (
  text: string,
  fresh: readonly string[],
  retract: MergeRetract | undefined,
): { readonly reason: MergeRetractReason; readonly iid?: number } | null => {
  if (retract === undefined || unitCovered(text, fresh)) return null;
  const u = saidOf(text);
  const rejected = (retract.rejected ?? []).find((r) => sameSaid(saidOf(r.text), u));
  if (rejected !== undefined) return { reason: `judge_${rejected.why}` };
  for (const b of retract.belongsTo ?? []) {
    const isRowOf = b.rows.some((r) => sameSaid(saidOf(r), u));
    if (isRowOf && covers(saidOf(b.says), u)) return { reason: "belongs_to", iid: b.iid };
  }
  return null;
};

/** Where a paragraph goes: after the last paragraph, else first. */
const paraSlot = (blocks: readonly Block[]): number => {
  let at = 0;
  blocks.forEach((b, i) => {
    if (b.kind === "para") at = i + 1;
  });
  return at;
};

/**
 * One owned section merged (rules 2, 5, 6): its new content, or undefined
 * when the current section stays as it is.
 */
const mergeSection = (
  name: string,
  current: string | null,
  fresh: string | null,
  restore: string | null,
  opts: MergeMeetingUpdateOptions,
  report: Report,
): string | undefined => {
  const blocks: Block[] = blocksOf(current).map((b) => (b.kind === "para" ? { ...b } : { ...b, rows: [...b.rows] }));
  const freshBlocks = blocksOf(fresh);
  const freshUnits = unitsOf(freshBlocks);
  let changed = false;

  // Rule 5 on the current units.
  const retractedHere: MergeRetracted[] = [];
  const flaggedHere: MergeRetracted[] = [];
  const stripLead = (text: string): string => {
    if (opts.retract?.ideaLead !== true) return text;
    const firstLine = text.split("\n")[0]!;
    const marker = /^\s*(?:[-*+]|\d+[.)])\s+/u.exec(firstLine)?.[0] ?? "";
    const stripped = stripLeadLabel(text.slice(marker.length));
    return stripped.stripped ? `${marker}${stripped.text}` : text;
  };
  const judge = (text: string): "keep" | "drop" => {
    const why = retractOf(text, freshUnits, opts.retract);
    if (why === null) return "keep";
    const unit = { section: name, text, ...why };
    if (!opts.loomOwned) {
      flaggedHere.push(unit);
      return "keep";
    }
    retractedHere.push(unit);
    return "drop";
  };
  const retractedBlocks: Block[] = [];
  for (const b of blocks) {
    if (b.kind === "para") {
      if (judge(b.text) === "drop") continue;
      retractedBlocks.push(b);
    } else {
      const rows = b.rows.filter((r) => judge(r) === "keep");
      if (rows.length > 0 || b.rows.length === 0) retractedBlocks.push({ ...b, rows });
      // A sentence written as the list's lead outlives its rows, as a paragraph.
      else if (isSentenceLead(b.lead)) retractedBlocks.push({ kind: "para", text: b.lead });
    }
  }
  // Never a whole section: when nothing would be left, nothing goes.
  const emptied = retractedBlocks.length === 0 && blocks.length > 0 && freshUnits.length === 0;
  if (emptied) {
    flaggedHere.push(...retractedHere.splice(0));
  } else if (retractedHere.length > 0) {
    blocks.splice(0, blocks.length, ...retractedBlocks);
    changed = true;
  }
  report.retracted.push(...retractedHere);
  report.flagged.push(...flaggedHere);
  // The idea lead of agreed work, stripped in place.
  for (const b of blocks) {
    const before = b.kind === "para" ? [b.text] : b.rows;
    const after = before.map(stripLead);
    after.forEach((t, i) => {
      if (t === before[i]) return;
      const unit = { section: name, text: before[i]!, reason: "idea_lead" as const };
      if (!opts.loomOwned) {
        report.flagged.push(unit);
        return;
      }
      report.retracted.push(unit);
      if (b.kind === "para") b.text = t;
      else b.rows[i] = t;
      changed = true;
    });
  }

  const everything = (): string[] => unitsOf(blocks);
  const addPara = (text: string, into: MergeUnit[], replace: boolean): void => {
    if (unitCovered(text, everything())) return;
    const u = saidOf(text);
    if (replace) {
      const hits = blocks.flatMap((b, i) => (b.kind === "para" && covers(u, saidOf(b.text)) ? [i] : []));
      if (hits.length > 0) {
        (blocks[hits[0]!] as { text: string }).text = text;
        for (const i of hits.slice(1).reverse()) blocks.splice(i, 1);
        into.push({ section: name, text });
        changed = true;
        return;
      }
    }
    blocks.splice(paraSlot(blocks), 0, { kind: "para", text });
    into.push({ section: name, text });
    changed = true;
  };
  const addRow = (lead: string | null, row: string, into: MergeUnit[], replace: boolean): void => {
    if (unitCovered(row, everything())) return;
    const lists = blocks.filter((b): b is Extract<Block, { kind: "list" }> => b.kind === "list");
    const list = [...lists].reverse().find((l) => leadKey(l.lead) === leadKey(lead));
    if (list !== undefined && replace) {
      const u = saidOf(row);
      const hits = list.rows.flatMap((r, i) => (covers(u, saidOf(r)) ? [i] : []));
      if (hits.length > 0) {
        list.rows[hits[0]!] = row;
        for (const i of hits.slice(1).reverse()) list.rows.splice(i, 1);
        into.push({ section: name, text: row });
        changed = true;
        return;
      }
    }
    if (list !== undefined) list.rows.push(row);
    else blocks.push({ kind: "list", lead, rows: [row] });
    into.push({ section: name, text: row });
    changed = true;
  };

  // Rule 2: the fresh units the section does not say.
  for (const b of freshBlocks) {
    if (b.kind === "para") addPara(b.text, report.added, opts.loomOwned);
    else for (const r of b.rows) addRow(b.lead, r, report.added, opts.loomOwned);
  }
  // Rule 6: what an earlier update dropped, on Loom's own bytes.
  if (opts.loomOwned) {
    for (const b of blocksOf(restore)) {
      const units = b.kind === "para" ? [b.text] : b.rows;
      for (const raw of units) {
        if (retractOf(raw, freshUnits, opts.retract) !== null) continue;
        const text = stripLead(raw);
        if (b.kind === "para") addPara(text, report.restored, false);
        else addRow(b.lead, text, report.restored, false);
      }
    }
  }

  // What the fresh render does not say, kept.
  const gone = new Set(retractedHere.map((r) => r.text));
  for (const u of unitsOf(blocksOf(current))) {
    if (!gone.has(u) && !unitCovered(u, freshUnits)) report.kept.push({ section: name, text: u });
  }
  if (!changed) return undefined;
  return textOfBlocks(blocks);
};

/** The block's row parts as the report names them. */
const ROW_PART_NAMES: Readonly<Record<MeetingTextLang, Readonly<Record<MeetingBlockRowPart, string>>>> = {
  de: { named: "Im Meeting genannt", quotes: "Zitate", outOfScope: "Nicht im Umfang" },
  en: { named: "Named in the meeting", quotes: "Quotes", outOfScope: "Not in scope" },
};

/** The block merged (rules 3, 5, 6): its new bytes, or undefined when it stays. */
const mergeBlock = (
  current: string,
  fresh: string | null,
  restore: string | null,
  opts: MergeMeetingUpdateOptions,
  report: Report,
): string | undefined => {
  const nameOfPart = (part: MeetingBlockRowPart): string => ROW_PART_NAMES[opts.lang][part];
  let block = patchMeetingBlock(current, opts.parts);
  const freshRows = fresh === null ? [] : meetingBlockRowsOf(fresh);
  const freshTexts = (part: MeetingBlockRowPart): string[] =>
    freshRows.filter((r) => r.part === part).map((r) => r.text);

  // Rule 5: a named or out-of-scope row Loom misplaced.
  const drop = new Set<string>();
  for (const row of meetingBlockRowsOf(block)) {
    if (row.part === "quotes") continue;
    const why = retractOf(row.text, freshTexts(row.part), opts.retract);
    if (why === null) continue;
    const unit = { section: nameOfPart(row.part), text: row.text, ...why };
    if (opts.loomOwned) {
      report.retracted.push(unit);
      drop.add(row.line);
    } else report.flagged.push(unit);
  }
  block = withoutMeetingBlockRows(block, drop);

  const union = (from: string, into: MergeUnit[]): void => {
    const u = unionMeetingBlockContent(block, from, blockCovered);
    block = u.block;
    into.push(...u.added.map((r: MeetingBlockRow) => ({ section: nameOfPart(r.part), text: r.text })));
  };
  for (const row of meetingBlockRowsOf(block)) {
    if (!freshRows.some((f) => f.part === row.part && blockCovered(row.part, row.text, [f.text]))) {
      report.kept.push({ section: nameOfPart(row.part), text: row.text });
    }
  }
  if (fresh !== null) union(fresh, report.added);
  if (opts.loomOwned && restore !== null) {
    // Nothing a retract would take out comes back.
    const back = new Set(
      meetingBlockRowsOf(restore)
        .filter((r) => r.part !== "quotes" && retractOf(r.text, freshTexts(r.part), opts.retract) !== null)
        .map((r) => r.line),
    );
    union(withoutMeetingBlockRows(restore, back), report.restored);
  }
  return block === current ? undefined : block;
};

/** The section's heading, or the block part's, for a unit the post-check misses. */
const sectionsOf = (parsed: ParsedIssueBody): string[] =>
  parsed.sections.map((s) => s.key ?? `?${nameOf(s.heading).toLowerCase()}`);

/** Rule 7: what the result lost of the current body (retracted units excepted). */
const droppedOf = (
  current: ParsedIssueBody,
  result: ParsedIssueBody,
  lang: MeetingTextLang,
  retracted: readonly MergeRetracted[],
): MergeUnit[] => {
  const out: MergeUnit[] = [];
  const gone = new Set(retracted.map((r) => r.text));
  const have = new Set(sectionsOf(result));
  for (const s of current.sections) {
    const id = s.key ?? `?${nameOf(s.heading).toLowerCase()}`;
    if (!have.has(id)) out.push({ section: nameOf(s.heading), text: s.body });
  }
  for (const key of MERGED_SECTIONS) {
    const after = sectionOf(result, key);
    const units = after === null ? [] : checkedUnitsOf(blocksOf(after.body));
    const anchors = anchorsOf(after?.body ?? "");
    // Every copy of a repeated heading: owning a key claims them all.
    for (const before of current.sections.filter((s) => s.key === key)) {
      for (const u of checkedUnitsOf(blocksOf(before.body))) {
        if (gone.has(u)) continue;
        const said = saidOf(u);
        const anchored = [...said.anchors].every((a) => anchors.has(a));
        if (!anchored || !unitCovered(u, units)) out.push({ section: nameOf(before.heading), text: u });
      }
    }
  }
  const beforeBlock = sectionOf(current, "from_meeting");
  const afterBlock = sectionOf(result, "from_meeting");
  if (beforeBlock !== null) {
    const rows = afterBlock === null ? [] : meetingBlockRowsOf(afterBlock.raw);
    for (const r of meetingBlockRowsOf(beforeBlock.raw)) {
      if (gone.has(r.text)) continue;
      const same = rows.filter((x) => x.part === r.part).map((x) => x.text);
      const kept =
        r.part === "quotes"
          ? same.some((t) => meetingQuoteText(t).toLowerCase() === meetingQuoteText(r.text).toLowerCase())
          : unitCovered(r.text, same);
      if (!kept) out.push({ section: ROW_PART_NAMES[lang][r.part], text: r.text });
    }
  }
  return out;
};

/** See the header. `current` and `fresh` are whole bodies; `fresh` carries no DoR marker. */
export const mergeMeetingUpdate = (
  current: string | null | undefined,
  fresh: string,
  opts: MergeMeetingUpdateOptions,
): MeetingUpdateMerge => {
  const text = (current ?? "").replace(/\r\n/gu, "\n");
  const cur = parseIssueBody(text);
  const frs = parseIssueBody(fresh.replace(/\r\n/gu, "\n"));
  const rst = opts.restore === undefined || opts.restore === null ? null : parseIssueBody(opts.restore);
  const report: Report = { kept: [], added: [], retracted: [], restored: [], flagged: [] };
  const owned: OwnedSections = {};

  for (const key of MERGED_SECTIONS) {
    const c = sectionOf(cur, key);
    const f = sectionOf(frs, key);
    const r = rst === null ? null : sectionOf(rst, key);
    if (c === null && f === null && r === null) continue;
    const name = nameOf((c ?? f ?? r)!.heading);
    const next = mergeSection(name, c?.body ?? null, f?.body ?? null, r?.body ?? null, opts, report);
    if (next !== undefined) owned[key] = next;
  }
  // Rule 3: a block a human removed is never written again.
  const block = sectionOf(cur, "from_meeting");
  if (block !== null) {
    const next = mergeBlock(
      block.raw,
      sectionOf(frs, "from_meeting")?.raw ?? null,
      rst === null ? null : (sectionOf(rst, "from_meeting")?.raw ?? null),
      opts,
      report,
    );
    if (next !== undefined) owned.from_meeting = next;
  }

  const body = Object.keys(owned).length === 0 ? text : renderIssueBody({ carried: cur, owned, headingLang: opts.lang });
  return {
    body,
    ...report,
    dropped: droppedOf(cur, parseIssueBody(body), opts.lang, report.retracted),
  };
};
